import React, { useEffect, useState } from "react";
import styles from "../scss/AppFormField.module.scss";
import { IAFSmartAvailableAnswer, IAnswerProps } from "../interfaces/IAppFormData";
import {
    checkDateNotSure,
    checkDateValueValid,
    checkYearForExtraInput,
    formatDate,
    getMonthListOptions,
    getYearListOptions,
    isDateInvalid
} from "../shared/AppFormHelper";
import Dropdown from "../../common/components/Forms/Dropdown/Dropdown";
import { IDropdownOption } from "../../common/interfaces/IDropdownOption";

interface IAFDateDropdownProps {
    question: any;
    availableQuestionAnswers: IAFSmartAvailableAnswer[];
    answerChanges: (answerParam: IAnswerProps) => void;
}

const AFDateDropdown: React.FC<IAFDateDropdownProps> = (props) => {
    const { question, availableQuestionAnswers, answerChanges } = props;

    const [yearValue, setYearValue] = useState(null);
    const [monthValue, setMonthValue] = useState(null);
    const [options, setOptions] = useState<IDropdownOption[]>([]);
    const [showMonthOption, setShowMonthOption] = useState(false);

    const handleInputTextValue = (value: string): void => {
        answerChanges({
            questionId: question.id,
            disclosure: [],
            answer: value
        });
    };

    const handleMonthValue = (value, year): void => {
        setMonthValue(value);
        if (value === "") {
            handleInputTextValue("");
            return;
        }
        const { dateFormat } = question;
        const date = new Date(year, value, 2);
        const dateFormatted = formatDate(date, dateFormat);
        if (isDateInvalid(dateFormatted)) {
            handleInputTextValue("");
            return;
        }
        handleInputTextValue(`M,${dateFormatted}`);
    };

    const handleDateDropdownValue = (value): void => {
        setYearValue(value);
        setShowMonthOption(false);
        if (value === "") {
            handleInputTextValue("");
            return;
        }
        if (value === "-1") {
            const otherOption =
                availableQuestionAnswers?.length &&
                availableQuestionAnswers[availableQuestionAnswers.length - 1];
            handleInputTextValue(otherOption?.value);
            return;
        }
        if (checkYearForExtraInput(value)) {
            setShowMonthOption(true);
            handleMonthValue(monthValue, value);
            return;
        }
        const { dateFormat } = question;
        if (value.includes("P")) {
            const year = value.replace("P,", "");
            handleInputTextValue(`P:${year},${formatDate(new Date(year), dateFormat)}`);
            return;
        }
        handleInputTextValue(`Y,${formatDate(new Date(value), dateFormat)}`);
    };

    const handleOptionList = (): void => {
        if (checkDateNotSure(availableQuestionAnswers)) {
            const otherOption =
                availableQuestionAnswers?.length &&
                availableQuestionAnswers[availableQuestionAnswers.length - 1];
            setOptions([
                {
                    label: otherOption?.displayText,
                    value: "-1"
                },
                ...getYearListOptions(question?.questionParameter, true)
            ]);
        } else {
            setOptions([...getYearListOptions(question?.questionParameter, false)]);
        }
    };

    const handleInitialValue = (answer): void => {
        if (!answer) return;
        if (!checkDateValueValid(new Date(answer))) {
            handleDateDropdownValue("-1");
            return;
        }
        const { dateFormat } = question;
        const splitDate = answer.split(",");
        const date = new Date(splitDate[1]);
        const value = formatDate(date, "yyyy");
        const isBeforeYear = splitDate[0].includes("P");
        setYearValue(isBeforeYear ? `P,${value}` : value);
        let initialValue = isBeforeYear ? `P:${value},` : "Y,";
        if (checkYearForExtraInput(value)) {
            handleInputTextValue("");
            setShowMonthOption(true);
            setMonthValue(date.getMonth());
            initialValue = "M,";
        }
        handleInputTextValue(`${initialValue}${formatDate(date, dateFormat)}`);
    };

    useEffect(() => {
        if (question?.id) {
            handleOptionList();
            handleInitialValue(question?.answer);
        }
    }, []);

    return (
        <div className={styles["af-dropdown"]} id={question.id}>
            <div className={styles["question-form-label"]}>Year</div>
            <Dropdown
                id={`dropdown-year-${question.id}`}
                name={`dropdown-year-${question.id}`}
                options={[
                    {
                        label: "",
                        value: ""
                    },
                    ...options
                ]}
                selectedOptionId={yearValue}
                onChange={(e) => {
                    handleDateDropdownValue(e.target.value);
                }}
                variant={"default"}
                className={styles["af-dropdown--style"]}
            />
            {showMonthOption && (
                <React.Fragment>
                    <div className={styles["question-form-label"]}>Month</div>
                    <Dropdown
                        id={`dropdown-month-${question.id}`}
                        name={`dropdown-month-${question.id}`}
                        options={[
                            {
                                label: "",
                                value: ""
                            },
                            ...getMonthListOptions()
                        ]}
                        selectedOptionId={monthValue}
                        onChange={(e) => {
                            handleMonthValue(e.target.value, yearValue);
                        }}
                        variant={"default"}
                        className={styles["af-dropdown--style"]}
                    />
                </React.Fragment>
            )}
        </div>
    );
};

export default AFDateDropdown;
